import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/services/api';
import { getDefaultPathForRole } from '@/config/routes';
import { takeLoginRedirect } from '@/utils/loginRedirect';

interface GoogleOAuthCallbacks {
  /** 가입 · 승인 대기 안내 한 덩어리 (에러가 아니다, #58) */
  onPending?: (message: string) => void;
  onError?: (error: string) => void;
}

/** 콜백 API 의 로그인 불가 결과(#51). 코드 이름은 LoginPage 의 `?error=` 안내와 같다 */
interface GoogleCallbackResult {
  result: string;
  params?: Record<string, string>;
}

// 승인 대기 · 초대 계정은 비밀번호로 들어가도 소용없으니 비밀번호 로그인 안내를 띄우지 않는다
const NO_PASSWORD_FALLBACK_RESULTS = ['pending_approval', 'invited_account'];

// 구글 로그인을 시작할 때 콘솔이 준 state 를 이 탭에 맡겨 둔다. 돌아왔을 때 다르면 다른 사람이 시작한 로그인이다 (#257)
const STATE_KEY = 'google_oauth_state';

const rememberState = (state: string): void => {
  try {
    sessionStorage.setItem(STATE_KEY, state);
  } catch (e) {
    console.warn('[google] sessionStorage 를 쓸 수 없습니다', e);
  }
};

/** 맡겨 둔 state 와 같은가. 한 번 꺼내면 지운다 */
const takeMatchingState = (returned: string | null): boolean => {
  try {
    const expected = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);
    return !!expected && expected === returned;
  } catch (e) {
    console.warn('[google] sessionStorage 를 읽을 수 없습니다', e);
    return false;
  }
};

const toLoginResultPath = ({ result, params = {} }: GoogleCallbackResult) =>
  `/login?${new URLSearchParams({ error: result, ...params }).toString()}`;

export const useGoogleOAuth = (customerId: string, callbacks?: GoogleOAuthCallbacks) => {
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const [googleProcessingMessage, setGoogleProcessingMessage] = useState('');
  const [showPasswordFallback, setShowPasswordFallback] = useState(false);
  
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const googleCallbackProcessed = useRef<string | null>(null);

  const handleGoogleCallback = useCallback(async (code: string, state?: string) => {
    setIsGoogleProcessing(true);
    setGoogleProcessingMessage('회원가입을 처리중입니다...');
    
    try {
      const response = await authAPI.googleCallback(code, state);
      
      // 201: 가입 · 승인 대기 {"result": "signup_pending", "params": {"is_new": ...}}
      if (response.status === 201) {
        const isNew = response.data?.params?.is_new === 'true';
        setIsGoogleProcessing(false);
        callbacks?.onPending?.(
          `${isNew ? '회원가입이 완료되었습니다.' : '이미 회원가입이 완료되었습니다.'}\n` +
          '관리자의 승인을 기다리고 있습니다. 승인되면 이메일로 알려드립니다.'
        );
        return;
      }
      
      const { access_token, user: userData, redirect_url } = response.data;
      
      if (!access_token || !userData) {
        throw new Error('로그인 응답에 토큰 또는 사용자 정보가 없습니다.');
      }
      
      setIsGoogleProcessing(false);
      login(access_token, userData);

      // 서비스 로그인(/oidc/authorize)에서 왔으면 그리로 먼저 돌아간다. 구글을 다녀오는 사이
      // 주소의 redirect 는 사라졌고, 로그인 화면이 맡겨 둔 것을 꺼낸다 (#226)
      const pending = takeLoginRedirect();
      if (pending) {
        navigate(pending);
        return;
      }

      // 리다이렉트 처리: 백엔드에서 제공한 redirect_url 사용, 없으면 대시보드로
      if (redirect_url) {
        // 전체 URL을 사용 (쿼리 파라미터 포함)
        // 같은 도메인이면 pathname + search 사용, 다른 도메인이면 전체 URL 사용
        try {
          const redirectUrlObj = new URL(redirect_url);
          const currentOrigin = window.location.origin;
          
          if (redirectUrlObj.origin === currentOrigin) {
            // 같은 도메인: pathname + search 사용
            const redirectPath = redirectUrlObj.pathname + redirectUrlObj.search;
            setTimeout(() => {
              navigate(redirectPath);
            }, 100);
          } else {
            // 다른 도메인: 전체 URL로 리다이렉트
            setTimeout(() => {
              window.location.href = redirect_url;
            }, 100);
          }
        } catch (e) {
          // URL 파싱 실패 시 pathname만 사용
          const redirectPath = redirect_url.startsWith('/') ? redirect_url : `/${redirect_url}`;
          setTimeout(() => {
            navigate(redirectPath);
          }, 100);
        }
      } else {
        // redirect_url이 없으면 역할에 맞는 기본 페이지로
        setTimeout(() => {
          navigate(getDefaultPathForRole(userData.role));
        }, 100);
      }
    } catch (error: any) {
      console.error('Google callback error:', error);

      // 로그인 불가 결과(거절 · 대기 · 구글 인증 실패)는 LoginPage 의 안내 문구로 보여준다
      const callbackResult: GoogleCallbackResult | undefined = error.response?.data?.result
        ? error.response.data
        : undefined;
      if (callbackResult) {
        setIsGoogleProcessing(false);
        setShowPasswordFallback(!NO_PASSWORD_FALLBACK_RESULTS.includes(callbackResult.result));
        navigate(toLoginResultPath(callbackResult), { replace: true });
        return;
      }

      let errorMessage = 'Google 로그인에 실패했습니다.';
      if (error.response?.status === 401) {
        errorMessage = error.response?.data?.detail || '인증에 실패했습니다.';
      } else if (error.response?.status === 403) {
        errorMessage = error.response?.data?.detail || '로그인 권한이 없습니다. 관리자에게 문의하세요.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setIsGoogleProcessing(false);
      setShowPasswordFallback(true);
      callbacks?.onError?.(errorMessage);
    }
  }, [login, navigate, callbacks]);

  const handleGoogleLogin = useCallback(async () => {
    try {
      const response = await authAPI.getGoogleAuthUrl(customerId || undefined);
      if (!response.data?.auth_url || !response.data?.state) {
        throw new Error('Google 인증 URL을 받아오지 못했습니다.');
      }
      rememberState(response.data.state);
      window.location.href = response.data.auth_url;
    } catch (error: any) {
      console.error('Google login error:', error);
      let errorMessage = 'Google 로그인 시작에 실패했습니다.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      callbacks?.onError?.(errorMessage);
    }
  }, [customerId, callbacks]);

  // Google OAuth 콜백 처리
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    // 중복 호출 방지: 같은 code는 한 번만 처리
    if (code && code !== googleCallbackProcessed.current) {
      googleCallbackProcessed.current = code;
      // 이 탭에서 시작한 로그인이 아니면 서버에 보내지 않는다 — 로그인 CSRF (#257)
      if (!takeMatchingState(state)) {
        navigate(toLoginResultPath({ result: 'invalid_state' }), { replace: true });
        return;
      }
      handleGoogleCallback(code, state || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, handleGoogleCallback]);

  return {
    isGoogleProcessing,
    googleProcessingMessage,
    showPasswordFallback,
    setShowPasswordFallback,
    handleGoogleLogin,
  };
};
